import { CardListItem } from "../../components/CardList/CardListItem";
import { SectionTitle } from "../../components/SettingsComponents/SectionTitle";
import { useBoardsSettings } from "../../data/queries/boardQueries";
import { InfoBar, Tab } from "@etframework/ui.components";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useModalEvent from "../../hooks/useModalEvent";
import emptyArchivedBoardImg from "../../content/img/archived-board-empty-state.svg";
import emptyBoardImg from "../../content/img/board-empty-state.svg";
import { useHasPendingBoardPermissionRequests } from "../../data/queries/boardPermissionRequestQueries";
import useRolePermissionStore from "../../store/rolePermissionStore";

export const BoardsList = () => {
  const navigate = useNavigate();
  const { data: boards } = useBoardsSettings();
  const { ModalEvents, toggleModal } = useModalEvent();
  const { t } = useTranslation(["SettingsLayout"], {
    useSuspense: false,
  });
  const rolePermissions = useRolePermissionStore(
    (state) => state.rolePermissions
  );
  const canAccessSettings = rolePermissions?.CanAccessSettings;

  const { t: requestsT } = useTranslation("ManageBoardPermissionRequests");

  const { data: hasPendingPermissions } =
    useHasPendingBoardPermissionRequests(canAccessSettings);

  const onCreateBoardClick = () => {
    toggleModal(ModalEvents.CreateBoardModal);
  };

  const EmptyBoardTemplate = ({ archived }) => {
    return (
      <div className="et-board-empty-state">
        <img src={archived ? emptyArchivedBoardImg : emptyBoardImg} />
        <div className="et-empty-state-msg">
          <span>
            {archived
              ? t("Boards.EmptyArchivedBoardMessage")
              : t("Boards.EmptyBoardMessage")}
          </span>
        </div>
      </div>
    );
  };

  const ActiveBoardList = ({ boards }) => {
    if (!boards) return;

    const activeBoards = boards?.filter((board) => !board.Archived);
    const _onClick = (id) => {
      navigate(`/duedates/settings/boards/${id}`);
    };

    return (
      <>
        {activeBoards && activeBoards.length > 0 ? (
          activeBoards.map((board, index) => (
            <CardListItem
              id={`dd_settings_boards-list_active_board-list-item-${index}-link`}
              key={index}
              board={board}
              onClick={_onClick}
            />
          ))
        ) : (
          <EmptyBoardTemplate archived={false} />
        )}
      </>
    );
  };

  const ArchivedBoardsList = ({ boards }) => {
    if (!boards) return;

    const archivedBoards = boards?.filter((board) => board.Archived);

    const _onClick = (id) => {
      navigate(`/duedates/settings/boards/${id}`);
    };

    return (
      <>
        {archivedBoards && archivedBoards.length > 0 ? (
          archivedBoards.map((board, index) => (
            <CardListItem
              id={`dd_settings_boards-list_archived_board-list-item-${index}-link`}
              key={index}
              board={board}
              onClick={_onClick}
            />
          ))
        ) : (
          <EmptyBoardTemplate archived={true} />
        )}
      </>
    );
  };

  return (
    <>
      <SectionTitle
        title={t("Boards.BoardsTitle")}
        rightBtn={
          <button
            id={"dd_settings_boards-list_create-board-button"}
            onClick={onCreateBoardClick}
            className="btn btn-primary create-board-btn"
          >
            <i className="far fa-plus pe-2"></i> {t("Boards.CreateBoard")}
          </button>
        }
      />
      {hasPendingPermissions && (
        <div className="mb-3">
          <InfoBar
            variant="warning"
            title={requestsT("Alert.Title")}
            message={requestsT("Alert.Message")}
            actions={[
              {
                label: requestsT("Alert.Action"),
                onClick: () => {
                  navigate("/manage/board-permission-requests");
                },
              },
            ]}
          />
        </div>
      )}
      <div className="module-layout-content h-100 et-board-list-scrollable-container">
        {boards && (
          <Tab
            tabNames={[t("Boards.Active"), t("Boards.Archived")]}
            tabComponents={[
              () => (
                <ActiveBoardList
                  key={"active-board-list"}
                  boards={boards?.filter((board) => !board?.Archived)}
                />
              ),
              () => (
                <ArchivedBoardsList
                  key={"archived-board-list"}
                  boards={boards?.filter((board) => !!board?.Archived)}
                />
              ),
            ]}
          />
        )}
      </div>
    </>
  );
};
